import { Layout as AntLayout, Menu, Dropdown, Select, List, Drawer } from "antd";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { MenuOutlined, CloseOutlined } from "@ant-design/icons";

import {
  faultyKioskIcon,
  navIcon,
  notificationIcon,
  unautherizedMovementIcon,
  zoneViolationIcon,
  zypLogo,
  cloudConnectivityIcon,
  geofenceReturnIcon,
  dashboardIcon,
  usersIcon,
  vehiclesIcon,
  batteryIcon,
  batterySwapIcon,
  settingsIcon,
  zonesIcon,
  issuesIcon,
  clientsIcon,
  notificationCenterIcon,
  tripsIcon,
} from "svgs";
import { ROUTES } from "utils/constants";
import {
  clearCommonState,
  markReadNotifications,
  setRefreshToken,
  setUsersEmpty,
  setVehicleStateEmpty,
  toggleLoading,
} from "store/actions";
import { AppState } from "store";
import { parseFcmNotificationTable } from "../../utils/parsers/fcm_notification_parser";
import { initializeNotification } from "../../store/actions/fcm_notification_actions";
import { USER_TYPE } from "../../utils/mapped-data";

const { Header: AntHeader } = AntLayout;

interface ComponentProps {
  toggleCollapse: () => void;
}

function getIconSpan(icon: JSX.Element) {
  return <span className="icon-span">{icon}</span>;
}

const navList = [
  {
    route: ROUTES.dashboard,
    label: "Dashboard",
    icon: getIconSpan(dashboardIcon),
  },
  { route: ROUTES.users, label: "Users", icon: getIconSpan(usersIcon) },
  {
    route: ROUTES.vehicles,
    label: "Vehicles",
    icon: getIconSpan(vehiclesIcon),
  },
  {
    route: ROUTES.battery,
    label: "Batteries",
    icon: getIconSpan(batteryIcon),
  },
  { route: ROUTES.kiosks, label: "Kiosks", icon: getIconSpan(batterySwapIcon) },
  { route: ROUTES.service_centres, label: "Service Centers", icon: getIconSpan(settingsIcon) },
  { route: ROUTES.zones, label: "Zones", icon: getIconSpan(zonesIcon) },
  { route: ROUTES.issues, label: "Issues", icon: getIconSpan(issuesIcon) },
  { route: ROUTES.clients, label: "Clients", icon: getIconSpan(clientsIcon) },
  { route: ROUTES.notifications, label: "Notifications", icon: getIconSpan(notificationCenterIcon) },
  { route: ROUTES.trips, label: "Trips", icon: getIconSpan(tripsIcon) },
];

function getRoleBasedNavList(userType: number) {
  const excludedLabelsByUserType: Record<number, string[]> = {
    3: ["Batteries", "Clients", "Vehicle Models", "Kiosks", "Trips", "Service Centers"],
    4: ["Batteries", "Clients", "Vehicle Models", "Kiosks", "Trips", "Service Centers"],
    5: ["Batteries", "Clients", "Vehicle Models", "Kiosks"],

    // userType 7,8
    7: ["Batteries", "Vehicle Models", "Zones", "Kiosks", "Trips", "Service Centers"],
    8: ["Batteries", "Vehicle Models", "Zones", "Trips", "Service Centers"],

    // userType 6
    6: ["Clients", "Vehicle Models", "Zones", "Trips"],
  };

  const excludedLabels = excludedLabelsByUserType[userType] ?? [];
  console.log("excludedLabels : ", excludedLabels)
  let navListOp = navList.filter(
    (navOption) => !excludedLabels.includes(navOption.label)
  );
  return navListOp
}

export default function AppHeader(props: ComponentProps) {
  const { toggleCollapse } = props;
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [cookies, setCookies, removeCookies] = useCookies();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const { userType, phone } = useSelector<AppState, AppState["common"]>(
    (state) => state.common
  );
  const { notifications } = useSelector<AppState, AppState["notifications"]>(
    (state) => state.notifications
  );

  const firstName = (cookies?.meta?.user?.firstName as string) || "";
  const lastName = (cookies?.meta?.user?.lastName as string) || "";
  const data = useMemo(
    () => parseFcmNotificationTable(notifications),
    [notifications]
  );

  const selectedTab = navList.find((tab) => {
    if (tab.route === "/zones") {
      return (
        pathname.includes(tab.route) ||
        pathname.includes("/add-zone") ||
        pathname.includes("/edit-zone")
      );
    }
    if (tab.route === "/vehicles") {
      return pathname.includes(tab.route) || pathname.includes("/vehicle_model");
    }
    return pathname.includes(tab.route);
  });

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(initializeNotification());
  }, [dispatch]);

  const clearAllState = async () => {
    await Promise.all([
      dispatch(clearCommonState()),
      dispatch(setUsersEmpty()),
      dispatch(setVehicleStateEmpty()),
    ]);
  };

  const logoutMethod = async () => {
    deleteAllCookies();
    removeCookies("meta", { path: "/" });
    dispatch(toggleLoading(true));
    dispatch(setRefreshToken(""));
    await clearAllState();
    window.location.href = ROUTES.login;
    dispatch(toggleLoading(false));
  };

  function deleteAllCookies() {
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

      document.cookie =
        name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      document.cookie =
        name +
        "=; Path=/; Domain=" +
        window.location.hostname +
        "; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    }
    localStorage.clear();
    sessionStorage.clear();
  }

  const onNotificationClick = (visible: boolean) => {
    if (visible) {
      let notificationIDs: string[] = [];
      data.forEach((notification) => {
        notificationIDs.push(notification.id);
      });
      if (notificationIDs.length > 0) {
        dispatch(markReadNotifications(notificationIDs));
      }
    } else if (!visible) {
      dispatch(initializeNotification());
    }
  };

  const onNavClick = useCallback(
    ({ key }) => {
      const clickedTab = navList.find((tab) => tab.label === key);
      if (clickedTab) {
        history.push(clickedTab.route);
        setMobileDrawerOpen(false);
      }
    },
    [history]
  );

  const getFormatedDate = (inputString: string) => {
    const inputDate = new Date(inputString);

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    return inputDate.toLocaleString("en-US", options);
  };

  const notification = (
    <>
      <List
        itemLayout="horizontal"
        dataSource={data}
        style={{
          width: isMobile ? "280px" : "250px",
          background: "#fff",
          maxHeight: "380px",
          overflow: "scroll",
          boxShadow: "rgb(0 0 0 / 30%) 1px 1px 16px -6px",
        }}
        renderItem={(item: any, index: any) => (
          <>
            <List.Item style={{ display: "block" }}>
              <List.Item.Meta
                avatar={
                  item.title == "Zone Violation"
                    ? zoneViolationIcon
                    : item.title == "Kiosk Faulty Slot"
                      ? faultyKioskIcon
                      : item.title == "Cloud Connectivity"
                        ? cloudConnectivityIcon
                        : item.title == "Geofence Return"
                          ? geofenceReturnIcon
                          : unautherizedMovementIcon
                }
                title={item.title}
                description={item.body}
                style={{ paddingLeft: "20px" }}
              />
              <List.Item.Meta
                style={{ textAlign: "center", paddingLeft: "25px" }}
                title={getFormatedDate(item.notification_time)}
              />
            </List.Item>
          </>
        )}
      />
    </>
  );

  const menu = (
    <Menu className="user-menu">
      <Menu.Item>
        <p>
          {firstName} {lastName}
        </p>
        {userType != undefined && <p>{USER_TYPE[userType]}</p>}
      </Menu.Item>
      <Menu.Item>
        <a onClick={logoutMethod} className="logout-menu">
          Logout
        </a>
      </Menu.Item>
    </Menu>
  );

  const mobileNavMenu = (
    <Menu
      className="nav-items mobile-nav-menu"
      theme="light"
      mode="inline"
      selectedKeys={[selectedTab?.label || "vehicles"]}
      onClick={onNavClick}
    >
      {getRoleBasedNavList(userType || 1).map((item) => (
        <Menu.Item className="__item" key={item.label} icon={item.icon}>
          {item.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      <AntHeader
        id="app-header"
        className={`app-header ${isMobile ? "app-header--mobile" : ""}`}
      >
        {isMobile ? (
          <span
            className="mobile-nav-toggle"
            onClick={() => setMobileDrawerOpen(true)}
          >
            <MenuOutlined />
          </span>
        ) : (
          <span className="nav-icon" onClick={toggleCollapse}>
            {navIcon}
          </span>
        )}
        <span className="--app-title-logo">{zypLogo}</span>

        <section className="dropdowns-container">
          <Dropdown
            overlay={notification}
            trigger={["click"]}
            overlayClassName="header-dropdown"
            onVisibleChange={onNotificationClick}
          >
            <span
              className="notification-icon"
              style={{ position: "relative", cursor: "pointer" }}
            >
              {notificationIcon}
              {data.length > 0 && (
                <span className="notification-badge">{data.length}</span>
              )}
            </span>
          </Dropdown>

          <Dropdown
            overlay={menu}
            trigger={["click"]}
            overlayClassName="header-dropdown"
          >
            <span className="menu-btn">{firstName[0]}</span>
          </Dropdown>
        </section>
      </AntHeader>

      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          visible={mobileDrawerOpen}
          className="mobile-nav-drawer"
          width={280}
          closeIcon={<CloseOutlined />}
        >
          <div className="logo mobile-logo" />
          {mobileNavMenu}
        </Drawer>
      )}

    </>
  );
}